import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { AlbumPage, Photo } from '../../data/coffeeTable';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { buildAlbumPages } from '../../utils/coffeeTable';
type Dir = -1 | 0 | 1;
type Slot = {
  page: AlbumPage;
  left: string;
  width: string;
  z: number;
  radius: string;
  shadow: string;
  transform: string;
  anim: string;
  shadeAnim: string;
};
const WIDE_TURN_MS = 820;
const NARROW_TURN_MS = 620;
const SWIPE_PX = 40;
const RADIUS_L = '10px 0 0 10px';
const RADIUS_R = '0 10px 10px 0';
const SHADOW_L = 'inset -16px 0 20px -14px rgba(74, 59, 79, 0.22)';
const SHADOW_R = 'inset 16px 0 20px -14px rgba(74, 59, 79, 0.22)';
const P = 'perspective(2200px) ';
const BACK = ' translateX(100%) rotateY(180deg)';
const EASE = ' 0.8s cubic-bezier(.45, .05, .3, 1) forwards';
const SHADE = 'ct-shade 0.8s ease';
function slot(page: AlbumPage, o: Partial<Slot>): Slot {
  return {
    page,
    left: '0',
    width: '100%',
    z: 1,
    radius: '10px',
    shadow: 'none',
    transform: 'none',
    anim: 'none',
    shadeAnim: 'none',
    ...o
  };
}

/** Lays out the visible pages. While a leaf turns, the page underneath is already drawn so the leaf can rotate over it. */
function computeSlots(pages: AlbumPage[], pos: number, turning: Dir, narrow: boolean): Slot[] {
  if (narrow) {
    const full = {
      left: '0',
      width: '100%',
      radius: '10px',
      shadow: 'none'
    };
    if (turning > 0) {
      return [slot(pages[pos + 1], full), slot(pages[pos], {
        ...full,
        z: 3,
        transform: 'perspective(1600px) rotateY(0deg)',
        anim: 'ct-single-fwd 0.6s ease-in forwards'
      })];
    }
    if (turning < 0) {
      return [slot(pages[pos], full), slot(pages[pos - 1], {
        ...full,
        z: 3,
        transform: 'perspective(1600px) rotateY(-110deg)',
        anim: 'ct-single-back 0.6s ease-out forwards'
      })];
    }
    return [slot(pages[pos], full)];
  }
  const leftHalf = {
    left: '0',
    width: '50%',
    radius: RADIUS_L,
    shadow: SHADOW_L
  };
  const rightHalf = {
    left: '50%',
    width: '50%',
    radius: RADIUS_R,
    shadow: SHADOW_R
  };
  const leafBack = {
    radius: RADIUS_L,
    shadow: SHADOW_L
  };
  if (turning > 0) {
    return [slot(pages[pos], leftHalf), slot(pages[pos + 3], rightHalf), slot(pages[pos + 1], {
      ...rightHalf,
      z: 3,
      transform: P + 'rotateY(0deg)',
      anim: 'ct-leaf-fwd-front' + EASE,
      shadeAnim: SHADE
    }), slot(pages[pos + 2], {
      ...rightHalf,
      ...leafBack,
      z: 3,
      transform: P + 'rotateY(0deg)' + BACK,
      anim: 'ct-leaf-fwd-back' + EASE,
      shadeAnim: SHADE
    })];
  }
  if (turning < 0) {
    return [slot(pages[pos - 2], leftHalf), slot(pages[pos + 1], rightHalf), slot(pages[pos - 1], {
      ...rightHalf,
      z: 3,
      transform: P + 'rotateY(-180deg)',
      anim: 'ct-leaf-back-front' + EASE,
      shadeAnim: SHADE
    }), slot(pages[pos], {
      ...rightHalf,
      ...leafBack,
      z: 3,
      transform: P + 'rotateY(-180deg)' + BACK,
      anim: 'ct-leaf-back-back' + EASE,
      shadeAnim: SHADE
    })];
  }
  return [slot(pages[pos], leftHalf), slot(pages[pos + 1], rightHalf)];
}
function Snapshot({
  photo
}: {
  photo: Photo;
}) {
  return <div className="ct-shot" style={{
    alignSelf: photo.align,
    transform: `rotate(${photo.rot}deg)`
  }} data-id="element-199" data-mp="33qpe31x8legh">
      <span aria-hidden="true" className="absolute -top-2.5 left-1/2 -ml-[37px] h-5 w-[74px] -rotate-[4deg] rounded-sm opacity-90" style={{
      background: photo.tape
    }} data-id="element-200" data-mp="2zp3yh8tbim2l" />
      <img className="mx-auto block h-[140px] w-auto max-w-full rounded-sm object-cover" src={photo.src} width={photo.width} height={photo.height} alt={photo.caption} loading="lazy" draggable={false} style={{
      background: photo.tint
    }} data-id="element-201" data-mp="270g7cwlw12h4" />
      <p className="ct-caption" data-id="element-202" data-mp="9qfznwynpj0">
        {photo.link ? <a className="text-[#1E4B48] underline decoration-[rgba(30,75,72,0.35)] underline-offset-[3px] hover:decoration-[#1E4B48]" href={photo.link} target="_blank" rel="noreferrer" data-id="element-203" data-mp="85n02qaoen9">
            {photo.caption}
          </a> : photo.caption}
      </p>
    </div>;
}
const NAV_PILL = 'inline-flex h-11 items-center justify-center gap-1.5 rounded-full border border-[#E3E6E3] bg-[#FFFFFF] text-sm font-medium text-[#1B2220] transition-[border-color,transform] duration-150 ease-out hover:border-[rgba(30,75,72,0.4)] active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[rgba(47,112,105,0.55)]';
export function PhotoAlbum() {
  const pages = useMemo(buildAlbumPages, []);
  const narrow = useMediaQuery('(max-width: 820px)');
  const [pos, setPos] = useState(0);
  const [turning, setTurning] = useState<Dir>(0);
  const timer = useRef<number>();
  const dragX = useRef<number | null>(null);

  // Switching between one-page and two-page views: snap to a spread start.
  useEffect(() => {
    window.clearTimeout(timer.current);
    setTurning(0);
    if (!narrow) setPos(p => p - p % 2);
  }, [narrow]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const step = narrow ? 1 : 2;
  const maxPos = pages.length - step;
  const atStart = pos === 0;
  const atEnd = pos >= maxPos;
  function turn(dir: -1 | 1) {
    if (turning) return;
    const target = pos + dir * step;
    if (target < 0 || target > maxPos) return;
    setTurning(dir);
    timer.current = window.setTimeout(() => {
      setPos(target);
      setTurning(0);
    }, narrow ? NARROW_TURN_MS : WIDE_TURN_MS);
  }
  function onPointerUp(e: React.PointerEvent) {
    if (dragX.current == null) return;
    const dx = e.clientX - dragX.current;
    dragX.current = null;
    if (dx < -SWIPE_PX) turn(1);else if (dx > SWIPE_PX) turn(-1);
  }
  const slots = computeSlots(pages, pos, turning, narrow);
  const pageLabel = narrow ? `Page ${pos + 1} of ${pages.length}` : `Pages ${pos + 1} and ${pos + 2} of ${pages.length}`;
  return <div className="rounded-3xl bg-[#F9E4DE] p-3 outline-dashed outline-2 -outline-offset-[10px] outline-[rgba(229,99,77,0.45)] md:p-6" data-id="element-204" data-mp="2hi37l1yyvcbt">
      <div className="w-full rounded-[20px] bg-[#1E4B48] p-3.5 shadow-[0_12px_24px_rgba(30,75,72,0.25)]" data-id="element-205" data-mp="23q5fm3xdzx2v">
        <div className="relative h-[540px] cursor-grab touch-pan-y select-none" onPointerDown={e => {
        dragX.current = e.clientX;
      }} onPointerUp={onPointerUp} onPointerCancel={() => {
        dragX.current = null;
      }} data-id="element-206" data-mp="1ttsbsd84o2ue">
          {slots.map((s, i) => <div key={i} className="ct-page" style={{
          left: s.left,
          width: s.width,
          zIndex: s.z,
          borderRadius: s.radius,
          boxShadow: s.shadow,
          transform: s.transform,
          animation: s.anim
        }} data-id="element-207" data-mp="1pt8f1argv18s" data-mpi={i}>
              {s.page.photos.map((ph, k) => <Snapshot key={k} photo={ph} data-id="element-208" data-mp="3u828se949r9k" data-mpi={k} />)}
              <span className="mt-auto self-center text-[11px] font-semibold text-[#4F5955]" data-id="element-209" data-mp="3pc4ra4m4aev7">{s.page.num}</span>
              <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[rgba(30,75,72,0.14)] opacity-0" style={{
            animation: s.shadeAnim
          }} data-id="element-210" data-mp="6xvsmcaql4gs" />
            </div>)}
          {!narrow && <div className="ct-spine" aria-hidden="true" data-id="element-211" data-mp="bv43drtk2680" />}
          <button type="button" className="ct-corner ct-corner-prev" onClick={() => turn(-1)} disabled={atStart} aria-label="Previous page" data-id="element-212" data-mp="byjnj92365ga" />
          <button type="button" className="ct-corner ct-corner-next" onClick={() => turn(1)} disabled={atEnd} aria-label="Next page" data-id="element-213" data-mp="lvehdenezho8" />
        </div>
      </div>
      <div className="mt-[22px] flex flex-wrap items-center justify-center gap-x-4 gap-y-3" data-id="element-214" data-mp="2nlpxyxjcv6km">
        <button type="button" className={`${NAV_PILL} pl-3 pr-[18px]`} onClick={() => turn(-1)} disabled={atStart} data-id="element-215" data-mp="2no2pa1b06tac">
          <ChevronLeftIcon className="h-4 w-4" aria-hidden data-id="element-216" data-mp="3h5hlx6osxtr1" />
          Back
        </button>
        <span className="min-w-[150px] text-center text-sm font-semibold text-[#1E4B48]" aria-live="polite" data-id="element-217" data-mp="3lwg5cf5377yi">
          {pageLabel}
        </span>
        <button type="button" className={`${NAV_PILL} pl-[18px] pr-3`} onClick={() => turn(1)} disabled={atEnd} data-id="element-218" data-mp="3pwrmvj25ks5r">
          Turn page
          <ChevronRightIcon className="h-4 w-4" aria-hidden data-id="element-219" data-mp="2ta53701u0yzl" />
        </button>
      </div>
    </div>;
}